import type { ReactNode } from 'react';

import { Button } from './Button';
import { Icon, type IconName } from './Icon';
import { LinkButton } from './LinkButton';

export interface CardProps {
  /** Rendered as the card's heading and used as its accessible name. */
  readonly title?: ReactNode;
  /** A line under the title. */
  readonly subtitle?: ReactNode;
  /** Controls in the card's header, right-aligned. */
  readonly actions?: ReactNode;
  /** Removes the body padding, for a card whose content is a table. */
  readonly flush?: boolean;
  /** The body. */
  readonly children: ReactNode;
}

/**
 * A bounded region of related content.
 *
 * Cards here are ruled, not floating: a hairline and a surface tint, no drop
 * shadow. Shadow in this system means "in front of the page" and is reserved
 * for overlays, so a screen full of shadowed cards would be a screen claiming
 * everything is in front of everything else.
 *
 * A titled card renders a `<section>` labelled by its heading, which puts it in
 * the landmark list a screen-reader user navigates by.
 *
 * @param props see {@link CardProps}
 * @returns the card
 */
export function Card({
  title,
  subtitle,
  actions,
  flush = false,
  children,
}: CardProps): React.JSX.Element {
  if (title === undefined) {
    return (
      <div className="fo-card">
        {flush ? children : <div className="fo-card__body">{children}</div>}
      </div>
    );
  }

  return (
    <section className="fo-card">
      <header className="fo-card__header">
        <div className="fo-card__heading">
          {/* h2, not h3. A card sits directly under the page's h1, so h3 would
              skip a level — which axe reports and which breaks heading
              navigation for anyone who moves through a page by headings. */}
          <h2 className="fo-card__title">{title}</h2>
          {subtitle === undefined ? null : <p className="fo-card__subtitle">{subtitle}</p>}
        </div>
        {actions === undefined ? null : <div className="fo-card__actions">{actions}</div>}
      </header>
      {flush ? children : <div className="fo-card__body">{children}</div>}
    </section>
  );
}

/**
 * The step an empty state puts first: a button that does it here, or a link
 * when it is done on another page.
 */
export type EmptyStateAction =
  | { readonly label: string; readonly onClick: () => void }
  | { readonly label: string; readonly to: string };

export interface EmptyStateProps {
  /** A glyph setting the context. */
  readonly icon?: IconName;
  /** What is not here. */
  readonly title: string;
  /** Why, and what to do about it. */
  readonly description: ReactNode;
  /**
   * The next step, drawn as the primary action. Omit it only when the person
   * looking has no step to take, such as a reader without permission to add.
   */
  readonly action?: EmptyStateAction;
  /** The primary action's label; superseded by `action`, which wins when both are given. */
  readonly actionLabel?: string;
  /** Called when the `actionLabel` button is used. */
  readonly onAction?: () => void;
}

function EmptyStateStep({ action }: { action: EmptyStateAction }): React.JSX.Element {
  if ('to' in action) {
    return (
      <LinkButton variant="primary" to={action.to}>
        {action.label}
      </LinkButton>
    );
  }
  return (
    <Button variant="primary" onClick={action.onClick}>
      {action.label}
    </Button>
  );
}

/**
 * The state a list is in before anyone has used it.
 *
 * Written as a sentence about what to do next rather than a shrug. "No jobs
 * yet" tells a new user nothing; "No jobs scheduled for Tuesday — drag one from
 * the unassigned tray, or add one" tells them where they are and what the next
 * move is.
 *
 * @param props see {@link EmptyStateProps}
 * @returns the empty state
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  actionLabel,
  onAction,
}: EmptyStateProps): React.JSX.Element {
  const step: EmptyStateAction | undefined =
    action ??
    (actionLabel === undefined || onAction === undefined
      ? undefined
      : { label: actionLabel, onClick: onAction });

  return (
    <div className="fo-empty">
      {icon === undefined ? null : <Icon name={icon} className="fo-empty__icon" size="2rem" />}
      <h2 className="fo-empty__title">{title}</h2>
      <p className="fo-empty__description">{description}</p>
      {step === undefined ? null : <EmptyStateStep action={step} />}
    </div>
  );
}

export interface SkeletonProps {
  /** How many placeholder lines to draw. */
  readonly lines?: number;
  /** What is loading, announced politely while it does. */
  readonly label: string;
}

/**
 * A loading placeholder.
 *
 * The bars are `aria-hidden`; the announcement is a separate visually hidden
 * line. A screen reader must not be asked to read grey rectangles, and a
 * sighted user must not be left with a page that looks finished but is not.
 * `aria-busy` on the wrapper is what tells assistive technology to expect the
 * content to change.
 *
 * @param props see {@link SkeletonProps}
 * @returns the placeholder
 */
export function Skeleton({ lines = 3, label }: SkeletonProps): React.JSX.Element {
  return (
    <div className="fo-skeleton" aria-busy="true">
      <span className="visually-hidden" role="status">
        {label}
      </span>
      {Array.from({ length: lines }, (_, index) => (
        <span key={index} className="fo-skeleton__line" aria-hidden="true" />
      ))}
    </div>
  );
}
